import { GiftTargetType, GiftTypes } from '../../types/userGift';

export const GIFT_TYPE_LABELS: Record<GiftTypes, string> = {
  [GiftTypes.FREE_MANAGEMENT_FEE]: 'Free management fee',
  [GiftTypes.PREMIUM]: 'Premium',
};

/** What each type does, shown at the top of the add dialog. */
export const GIFT_TYPE_DESCRIPTIONS: Record<GiftTypes, string> = {
  [GiftTypes.FREE_MANAGEMENT_FEE]:
    'A portfolio opened before the expiry date never pays a management fee. Portfolios opened earlier keep paying. ' +
    "The person doesn't need an account yet.",
  [GiftTypes.PREMIUM]:
    'Premium for the chosen number of months, starting the day it is applied (the next time the person opens the ' +
    'app). Used once, by one person, and a person gets one Premium gift only. Not applied while they are already ' +
    'Premium; when it ends they get the normal 7-day grace. The expiry date is the last day it can be applied.',
};

/** Same bounds madkhol-user enforces (`PREMIUM_GIFT_MIN_MONTHS` / `PREMIUM_GIFT_MAX_MONTHS`). */
export const PREMIUM_MONTHS_MIN = 1;
export const PREMIUM_MONTHS_MAX = 36;

export const TARGET_TYPE_LABELS: Record<GiftTargetType, string> = {
  [GiftTargetType.USER_ID]: 'Existing user',
  [GiftTargetType.NATIONAL_ID]: 'National ID',
  [GiftTargetType.EMAIL]: 'Email',
  [GiftTargetType.PHONE]: 'Phone',
};

export const TARGET_VALUE_HINTS: Record<GiftTargetType, string> = {
  [GiftTargetType.USER_ID]: 'The user id from the user page',
  [GiftTargetType.NATIONAL_ID]: '10 digits, matched against the KYC national id',
  [GiftTargetType.EMAIL]: "Matched against the user's account email",
  [GiftTargetType.PHONE]: 'e.g. +9665XXXXXXXX or 05XXXXXXXX, matched against the account mobile',
};

/*
 * The same shape checks madkhol-user applies (`src/helpers/user-gift.helper.ts`), so the form can explain a problem
 * before the round trip. The server normalizes and stays the authority.
 */
export function validateGiftTarget(targetType: GiftTargetType, raw: string): string | undefined {
  const value = raw.trim();

  if (!value) {
    return 'Required';
  }

  switch (targetType) {
    case GiftTargetType.USER_ID:
      return /^[1-9]\d*$/.test(value) ? undefined : 'Enter a user id';
    case GiftTargetType.NATIONAL_ID:
      return /^\d{10}$/.test(value) ? undefined : 'A national id is 10 digits';
    case GiftTargetType.EMAIL:
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? undefined : 'Enter a valid email';
    case GiftTargetType.PHONE:
      return /^\+?\d[\d\s\-()]{7,}$/.test(value) ? undefined : 'Enter a valid phone number';
    default:
      return undefined;
  }
}

const GIFT_ERRORS: Record<string, string> = {
  GIFT_TYPE_NOT_FOUND: 'This gift type does not exist.',
  GIFT_TYPE_INACTIVE: 'This gift type is switched off.',
  INVALID_GIFT_TARGET: 'The value is not valid for the chosen target.',
  USER_NOT_FOUND: 'No user with this id.',
  EXPIRES_AT_IN_PAST: 'The expiry must be in the future.',
  GIFT_ALREADY_EXISTS: 'An active gift of this type already exists for this target.',
  PREMIUM_MONTHS_REQUIRED: 'Enter the number of Premium months.',
  PREMIUM_MONTHS_NOT_ALLOWED: 'Premium months only apply to a Premium gift.',
  USER_ALREADY_PREMIUM: 'This user is already Premium.',
  USER_ALREADY_USED_PREMIUM_GIFT: 'This user has already used a Premium gift.',
  NOT_FOUND: 'This gift no longer exists.',
};

/** Reads the reason code the gateway passes through from madkhol-user. */
export const describeGiftError = (error: any): string => {
  const message = error?.data?.message;

  return GIFT_ERRORS[message] || message || 'The gift could not be saved.';
};

export const isExpired = (expiresAt: string) => new Date(expiresAt).getTime() <= Date.now();
